import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dropdown,
  Box,
  Loader,
  Modal,
  Flex,
  Text,
  Button,
  IconSvg,
  IconSvgButton,
  List,
  CriteriaTag,
  InputWithLabel
} from "samba-ui";
import styled from "styled-components";
import { validateEmail } from "../utils/validators";
import { loadAllAdminDashboards, inviteUser } from "../api";
import SetFilters from "./SetFilters";
import ShowPeriscopeUrl from "./ShowPeriscopeUrl";
import getPeriscopeUrlParams from "../utils/getPeriscopeUrlParams";
const applicationOptions = [
  { label: "Real-time TV Viewership", value: "command-center" },
  { label: "Samba TV Analytics", value: "samba-data-analytics" },
  { label: "RT CID TV Integrations", value: "rt-cid-tv-integrations-webpage" }
];

const StyledIconSvg = styled(IconSvg)`
  cursor: pointer;
  float: right;
`;

const StyledText = styled(Text)`
  cursor: pointer;
`;

const InviteUserModal = ({ isOpen, onClose, onSubmit }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({
    firstName: null,
    lastName: null,
    email: null
  });
  const [loading, setLoading] = useState(false);

  const [isSambaEmail, setIsSambaEmail] = useState(false);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState({});

  const [selectedFilters, setSelectedFilters] = useState(null);
  const [showPeriscopeUrl, setShowPeriscopeUrl] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState("");
  const [
    selectedDashboardsWithFilter,
    setSelectedDashboardsWithFilter
  ] = useState([]);

  const onAddAnotherDashboardClick = () => {
    const dashboard = selectedDashboard;
    dashboard.filters = selectedFilters;
    const data = [...selectedDashboardsWithFilter, dashboard];
    setSelectedDashboardsWithFilter(data);
    setSelectedDashboard(null);
    setSelectedFilters(null);
    setShowPeriscopeUrl(false);
  };

  const onDeleteSelectedDashboards = index => {
    const copy = selectedDashboardsWithFilter.slice();
    copy.splice(index, 1); // remove the index
    setSelectedDashboardsWithFilter(copy);
  };

  const sambaEmailErrorMsg = "Samba TV employees don't need invitation.";

  const [dashboards, setDashboards] = useState({});

  useEffect(() => {
    const req = async () => {
      const res = await loadAllAdminDashboards();
      const { data } = await res.json();
      setDashboards(data);
    };
    if (
      selectedApplication &&
      selectedApplication.value === "samba-data-analytics"
    ) {
      req();
    }
  }, [selectedApplication]);

  const clearState = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setSelectedApplication(null);
    setSelectedDashboard({});
    setSelectedFilters(null);
    setShowPeriscopeUrl(false);
    setSelectedDashboardsWithFilter([]);
    setShowError("");
    setShowSuccess(false);
  };

  const onRequestClose = () => {
    clearState();
    onClose();
  };

  const validateFirstNameField = value => {
    return value ? null : "First name is required";
  };

  const validateLastNameField = value => {
    return value ? null : "Last name is required";
  };

  const validateEmailField = value => {
    const isEmailValid = validateEmail(value);

    const isSambaEmailDomain = value.indexOf("@samba.tv") > -1;
    setIsSambaEmail(isSambaEmailDomain);

    if (!value) {
      return "Email is required";
    }

    if (isSambaEmailDomain) {
      return sambaEmailErrorMsg;
    }

    if (!isEmailValid) {
      return "Invalid Email";
    }

    return null;
  };

  const onFirstNameChange = e => {
    const { value } = e.target;
    setFirstName(value);

    setErrors({ ...errors, firstName: validateFirstNameField(value) });
  };

  const onFirstNameFocus = () => {
    setErrors({ ...errors, firstName: null });
  };

  // Validation for UI purposes takes place onBlur
  const onFirstNameBlur = e => {
    const { value } = e.target;
    setErrors({
      ...errors,
      firstName: validateFirstNameField(value)
    });
  };

  const onLastNameChange = e => {
    const { value } = e.target;
    setLastName(value);

    setErrors({ ...errors, lastName: validateLastNameField(value) });
  };

  const onLastNameFocus = () => {
    setErrors({ ...errors, lastName: null });
  };

  // Validation for UI purposes takes place onBlur
  const onLastNameBlur = e => {
    const { value } = e.target;
    setErrors({
      ...errors,
      lastName: validateLastNameField(value)
    });
  };

  const onEmailChange = e => {
    const { value } = e.target;
    setEmail(value);

    const isSambaEmailDomain = value.indexOf("@samba.tv") > -1;
    setIsSambaEmail(isSambaEmailDomain);

    // Trigger the samba email error immediately on valid samba email.
    setErrors({
      ...errors,
      email: isSambaEmailDomain ? sambaEmailErrorMsg : null
    });
  };

  // Clear errors on focus
  const onEmailFocus = () => {
    setErrors({ ...errors, email: null });
    setIsSambaEmail(false);
  };

  // Validation (only for UI purposes) onBlur
  const onEmailBlur = e => {
    const { value } = e.target;
    setErrors({
      ...errors,
      email: validateEmailField(value)
    });
  };

  const validateForm = () => {
    setErrors({
      ...errors,
      firstName: validateFirstNameField(firstName),
      lastName: validateLastNameField(lastName),
      email: validateEmailField(email)
    });
  };

  const onSignupClick = async e => {
    e.preventDefault();
    if (isDisabled()) {
      validateForm();
      return;
    }

    setLoading(true);

    const dashboards = selectedDashboardsWithFilter.reduce((acc, s) => {
      acc[s.value] = s.filters.map(f => f.value);
      return acc;
    }, {});
    const body = {
      firstName,
      lastName,
      email,
      dashboards,
      application: selectedApplication.value
    };

    const res = await inviteUser(body);
    const { error } = await res.json();

    setLoading(false);
    if (error) {
      setShowError(error);
    } else {
      setShowSuccess(true);
    }
  };

  const isDisabled = () =>
    !firstName || !lastName | !(email && validateEmail(email)) || isSambaEmail;

  const dashboardDropdownOptions = Object.keys(dashboards).map(id => {
    return {
      label: dashboards[id].name,
      value: id
    };
  });

  const getContent = () => {
    return (
      <Flex alignItems="center" justifyContent="center" mt={4}>
        {showError ? (
          <Text color="alert">
            The operation is not successful. It is failing because {showError}{" "}
            <StyledText color="primary" onClick={() => setShowError("")}>
              click here to start over
            </StyledText>
          </Text>
        ) : showSuccess ? (
          <Text>
            Operation is successful. Please close the modal or{" "}
            <StyledText color="primary" onClick={() => clearState()}>
              click here to start over
            </StyledText>
          </Text>
        ) : (
          <Loader />
        )}
      </Flex>
    );
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} width={800}>
      <Modal.Header borderColor="#d6d6d6 !important">
        <Text>Invite a user</Text>
      </Modal.Header>
      <Modal.Content minHeight={350}>
        {showSuccess || showError || loading ? (
          getContent()
        ) : (
          <Box>
            <Flex alignItems="center" justifyContent="space-between">
              <Box mb={3} mr={3} width={1 / 2}>
                <InputWithLabel
                  placeholder="First name"
                  labelTitle="First name"
                  value={firstName}
                  error={errors.firstName}
                  onChange={onFirstNameChange}
                  onFocus={onFirstNameFocus}
                  onBlur={onFirstNameBlur}
                  style={{ width: "100%" }}
                />
              </Box>
              <Box mb={3} width={1 / 2}>
                <InputWithLabel
                  placeholder="Last name"
                  labelTitle="Last name"
                  value={lastName}
                  error={errors.lastName}
                  onChange={onLastNameChange}
                  onFocus={onLastNameFocus}
                  onBlur={onLastNameBlur}
                  style={{ width: "100%" }}
                />
              </Box>
            </Flex>
            <Flex alignItems="center" justifyContent="space-between">
              <Box mb={3} mr={3} width={1 / 2}>
                <InputWithLabel
                  placeholder="name@company.com"
                  value={email}
                  labelTitle="Company provided email"
                  error={errors.email}
                  onChange={onEmailChange}
                  onBlur={onEmailBlur}
                  onFocus={onEmailFocus}
                  style={{ width: "100%" }}
                />
              </Box>
              <Box mb={3} width={1 / 2}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500} mb={2}>
                    Select an application
                  </Text>
                </Box>
                <Dropdown
                  options={applicationOptions}
                  selected={selectedApplication}
                  onSelect={setSelectedApplication}
                />
              </Box>
            </Flex>
            {selectedApplication &&
            selectedApplication.value === "samba-data-analytics" ? (
              <Box mb={3}>
                <Box mb={2}>
                  <Text fontSize={0} fontWeight={500}>
                    Assign a dashboard
                  </Text>
                </Box>
                <Dropdown
                  options={dashboardDropdownOptions}
                  selected={selectedDashboard}
                  onSelect={selected => {
                    // find the defaults and set there for selected filters
                    const defaultFilters = dashboards[selected.value].filters
                      .filter(f => f.isDefault)
                      .map(f => ({
                        label: f.name,
                        value: f.id,
                        removeIcon: true
                      }));

                    setSelectedDashboard(selected);
                    setSelectedFilters(defaultFilters);
                    setShowPeriscopeUrl(false);
                  }}
                />

                {selectedDashboardsWithFilter.map((dashboard, index) => (
                  <List.Item key={index} borderColor="#d6d6d6 !important">
                    <Flex alignItems="center" justifyContent="space-between">
                      <Box width={1 / 6}>
                        <Text>{dashboard.label}</Text>
                      </Box>
                      <Flex width={4 / 6}>
                        {dashboard.filters.map(filter => (
                          <Box mr={2} key={filter.label}>
                            <CriteriaTag>{filter.label}</CriteriaTag>
                          </Box>
                        ))}
                      </Flex>
                      <Box width={1 / 6}>
                        <StyledIconSvg
                          name="close"
                          color="red"
                          onClick={() => onDeleteSelectedDashboards(index)}
                        />
                      </Box>
                    </Flex>
                  </List.Item>
                ))}

                {selectedDashboard && (
                  <Box mt={2}>
                    <SetFilters
                      filters={
                        (dashboards[selectedDashboard.value] &&
                          dashboards[selectedDashboard.value].filters) ||
                        []
                      }
                      selectedFilters={selectedFilters}
                      setSelectedFilters={filters => {
                        setShowPeriscopeUrl(false);
                        setSelectedFilters(filters);
                      }}
                    />
                  </Box>
                )}

                {selectedFilters && (
                  <Flex mt={3} alignItems="center" justifyContent="flex-end">
                    <Box mr={3}>
                      {showPeriscopeUrl ? (
                        <ShowPeriscopeUrl
                          {...getPeriscopeUrlParams(
                            dashboards[selectedDashboard.value],
                            selectedFilters
                          )}
                        />
                      ) : (
                        <IconSvgButton
                          name="check-o"
                          variant="destructiveBordered"
                          onClick={() => setShowPeriscopeUrl(true)}
                        >
                          Test before adding
                        </IconSvgButton>
                      )}
                    </Box>
                    <IconSvgButton
                      name="add-o"
                      variant="primaryBordered"
                      onClick={onAddAnotherDashboardClick}
                    >
                      Add another dashboard
                    </IconSvgButton>
                  </Flex>
                )}
              </Box>
            ) : null}
          </Box>
        )}
      </Modal.Content>
      <Modal.Actions justifyContent="flex-end" borderColor="#d6d6d6 !important">
        <Flex>
          <Box mr={3}>
            <Button variant="secondary" onClick={onRequestClose}>
              Cancel
            </Button>
          </Box>
          <Button
            variant="primary"
            disabled={isDisabled()}
            onClick={onSignupClick}
          >
            Send Invitation
          </Button>
        </Flex>
      </Modal.Actions>
    </Modal>
  );
};

InviteUserModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default InviteUserModal;
